import React from 'react';

import SimpleGrid from '~/common/components/Grids/Grid/SimpleGrid';
import HStack from '~/common/components/Stacks/HStack';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import BreakdownContainer from '~/pages/IncomeCalculator/Pages/ReviewBreakdown/BreakdownContainer';
import { CHILD_SUPPORT_CALCULATION_TYPES } from '~/pages/IncomeCalculator/constants';
import type { CalculationResult } from '~/pages/IncomeCalculator/types';

type CalculationBreakdownProps = {
  selectedResult?: CalculationResult;
  hideAccordion?: boolean;
  monthlyAmount?: number;
  monthlyFormula?: string;
};

function CalculationBreakdown({
  selectedResult,
  hideAccordion = false,
  monthlyAmount,
  monthlyFormula,
}: Readonly<CalculationBreakdownProps>) {
  const displayAmount = monthlyAmount ?? selectedResult.calculationAmount;

  // Use provided formula or extract from selectedResult
  const fullFormula =
    monthlyFormula ||
    (() => {
      const formulas = selectedResult.formulas;
      const monthlyIncomeFormula =
        formulas.find(
          (f) =>
            f.formulaName?.toLowerCase().includes('monthly') ||
            f.formulaName?.toLowerCase().includes('income'),
        ) || formulas[0];
      return monthlyIncomeFormula?.formula || '';
    })();

  // Extract the equation part before the equals sign for display
  const formulaParts = fullFormula.split('=');
  const formulaEquation = formulaParts[0]?.trim() || '';

  return (
    <BreakdownContainer
      header={`${
        CHILD_SUPPORT_CALCULATION_TYPES[selectedResult.calculatorTypeId]
      } Review Breakdown`}
      hideAccordion={hideAccordion}
    >
      <SimpleGrid columns={1} spacingY={6} mt={2}>
        <HStack>
          <Text mr={1} fontSize="sm" fontWeight="bold">
            Monthly Income Equation:
          </Text>
          <Text fontSize="sm" data-testid="calculator-type">
            (Monthly Amount) * {formulaEquation.split('*')[1]?.trim() || ''}
          </Text>
        </HStack>
        <HStack>
          <Text mr={1} fontSize="sm" fontWeight="bold">
            Calculation:
          </Text>
          <Text fontSize="sm" data-testid="formula-name">
            {fullFormula}
          </Text>
        </HStack>
        <HStack>
          <Text mr={1} fontSize="sm" fontWeight="bold">
            Monthly Income:
          </Text>
          <Text fontSize="sm" data-testid="formula-name">
            {formatCurrency(displayAmount)}
          </Text>
        </HStack>
      </SimpleGrid>
    </BreakdownContainer>
  );
}

const CalculationBreakdownMemo = React.memo(CalculationBreakdown);
export default CalculationBreakdownMemo;
